import { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Empty, Page, PageHeader } from "@/components/common";
import { api, errMsg } from "@/lib/api";

const CUSTOM = "__custom";
const localDate = () => {
  const current = new Date();
  current.setMinutes(current.getMinutes() - current.getTimezoneOffset());
  return current.toISOString().slice(0, 10);
};
const blank = () => ({
  borrower_name: "",
  borrowing_department: "",
  equipment_id: "",
  equipment_name: "",
  borrowed_date: localDate(),
  notes: "",
});
const displayDate = (value) =>
  new Date(`${value.slice(0, 10)}T12:00:00`).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });

function BorrowedDialog({ record, inventory, onClose, onSaved }) {
  const [form, setForm] = useState({ ...blank(), ...record });
  const [busy, setBusy] = useState(false);
  const equipment = inventory.filter((item) => item.item_type === "equipment");
  const linkedEquipment = equipment.find(
    (item) => item.id === form.equipment_id,
  );
  const selected = linkedEquipment ? form.equipment_id : CUSTOM;
  const set = (key) => (value) =>
    setForm((current) => ({ ...current, [key]: value }));
  const chooseEquipment = (value) => {
    if (value === CUSTOM) {
      setForm((current) => ({ ...current, equipment_id: "" }));
      return;
    }
    const item = equipment.find((entry) => entry.id === value);
    setForm((current) => ({
      ...current,
      equipment_id: value,
      equipment_name: item?.name || "",
    }));
  };
  const save = async () => {
    setBusy(true);
    try {
      if (record?.id) {
        await api.put(`/borrowed-equipment/${record.id}`, form);
        toast.success("Borrowed equipment updated");
      } else {
        await api.post("/borrowed-equipment", form);
        toast.success("Borrowed equipment logged");
      }
      onSaved();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-lg"
        data-testid="borrowed-equipment-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {record?.id ? "Edit borrowed equipment" : "Log borrowed equipment"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label>Borrower name</Label>
            <Input
              value={form.borrower_name}
              onChange={(event) => set("borrower_name")(event.target.value)}
              data-testid="borrowed-borrower-input"
            />
          </div>
          <div className="space-y-1">
            <Label>Borrowing department</Label>
            <Input
              value={form.borrowing_department}
              onChange={(event) =>
                set("borrowing_department")(event.target.value)
              }
              data-testid="borrowed-department-input"
            />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label>Equipment</Label>
            <Select value={selected} onValueChange={chooseEquipment}>
              <SelectTrigger data-testid="borrowed-equipment-select">
                <SelectValue placeholder="Choose equipment" />
              </SelectTrigger>
              <SelectContent>
                {equipment.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.name}
                  </SelectItem>
                ))}
                <SelectItem value={CUSTOM}>Other equipment</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!linkedEquipment && (
            <div className="space-y-1 sm:col-span-2">
              <Label>Equipment name</Label>
              <Input
                value={form.equipment_name}
                onChange={(event) => set("equipment_name")(event.target.value)}
                data-testid="borrowed-equipment-name-input"
              />
            </div>
          )}
          <div className="space-y-1">
            <Label>Date borrowed</Label>
            <Input
              type="date"
              value={form.borrowed_date}
              onChange={(event) => set("borrowed_date")(event.target.value)}
              data-testid="borrowed-date-input"
            />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label>Notes</Label>
            <Textarea
              rows={3}
              value={form.notes}
              onChange={(event) => set("notes")(event.target.value)}
              data-testid="borrowed-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            data-testid="borrowed-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={
              busy ||
              !form.borrower_name.trim() ||
              (!form.equipment_id && !form.equipment_name.trim())
            }
            data-testid="borrowed-save-button"
          >
            {busy ? "Saving…" : "Save entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BorrowedRow({ record, onEdit, onReturn }) {
  return (
    <div
      className="grid gap-3 p-4 sm:grid-cols-[1.4fr_1fr_1fr_auto] sm:items-center"
      data-testid={`borrowed-row-${record.id}`}
    >
      <div className="min-w-0">
        <div
          className="font-medium"
          data-testid={`borrowed-equipment-${record.id}`}
        >
          {record.equipment_name}
        </div>
        {record.notes && (
          <div className="mt-0.5 text-xs text-muted-foreground">
            {record.notes}
          </div>
        )}
      </div>
      <div>
        <div data-testid={`borrowed-borrower-${record.id}`}>
          {record.borrower_name}
        </div>
        <div className="text-xs text-muted-foreground">
          {record.borrowing_department || "No department recorded"}
        </div>
      </div>
      <div
        className="text-sm text-muted-foreground"
        data-testid={`borrowed-date-${record.id}`}
      >
        Borrowed {displayDate(record.borrowed_date)}
        {record.returned_at && (
          <div className="mt-1 text-[11px]">
            Returned {displayDate(record.returned_at)} · {record.returned_by}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 sm:justify-end">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={record.returned}
            onChange={(event) => onReturn(record, event.target.checked)}
            className="h-4 w-4 accent-primary"
            data-testid={`borrowed-returned-checkbox-${record.id}`}
          />
          <span>{record.returned ? "Returned" : "Still out"}</span>
        </label>
        {!record.returned && (
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onEdit(record)}
            title="Edit borrowed item"
            data-testid={`borrowed-edit-${record.id}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default function BorrowedEquipmentPage() {
  const [scope, setScope] = useState("open");
  const [records, setRecords] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => {
    api
      .get("/borrowed-equipment", { params: { status: scope } })
      .then((response) => setRecords(response.data))
      .catch((error) => toast.error(errMsg(error)));
  }, [scope]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    api
      .get("/inventory")
      .then((response) => setInventory(response.data))
      .catch(() => {});
  }, []);
  const shown = useMemo(() => {
    const words = query.trim().toLowerCase();
    if (!words) return records;
    return records.filter((record) =>
      `${record.borrower_name} ${record.borrowing_department} ${record.equipment_name}`
        .toLowerCase()
        .includes(words),
    );
  }, [query, records]);
  const toggleReturned = async (record, checked) => {
    try {
      await api.post(`/borrowed-equipment/${record.id}/returned`, {
        returned: checked,
      });
      toast.success(
        checked ? "Marked as returned" : "Marked as still borrowed",
      );
      load();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  return (
    <Page wide>
      <PageHeader
        title="Borrowed equipment"
        subtitle="Items loaned to other departments."
      >
        <Button
          onClick={() => setEditing({})}
          data-testid="borrowed-add-button"
        >
          <Plus className="mr-2 h-4 w-4" />
          Log borrowed item
        </Button>
      </PageHeader>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Tabs value={scope} onValueChange={setScope}>
          <TabsList>
            <TabsTrigger value="open" data-testid="borrowed-tab-open">
              Out now
            </TabsTrigger>
            <TabsTrigger value="returned" data-testid="borrowed-tab-returned">
              Returned
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="borrowed-tab-all">
              All
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative w-full sm:max-w-xs">
          <Search
            className={
              "absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 " +
              "text-muted-foreground"
            }
          />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="pl-9"
            placeholder="Search borrower or equipment"
            data-testid="borrowed-search-input"
          />
        </div>
      </div>
      {shown.length === 0 ? (
        <Empty>
          {scope === "open"
            ? "No equipment is currently recorded as borrowed."
            : "No borrowed-equipment records found."}
        </Empty>
      ) : (
        <div
          className="divide-y rounded-xl border bg-card"
          data-testid="borrowed-table"
        >
          {shown.map((record) => (
            <BorrowedRow
              key={record.id}
              record={record}
              onEdit={setEditing}
              onReturn={toggleReturned}
            />
          ))}
        </div>
      )}
      {editing && (
        <BorrowedDialog
          record={editing.id ? editing : null}
          inventory={inventory}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </Page>
  );
}
